"use client";

import { ErrorNotice } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { downloadText } from "@/lib/tool-kit/download";
import {
  codebookCsv,
  excerptsCsv,
  excerptsFor,
  excerptsMarkdown,
  type Project,
  serializeProject,
} from "@/lib/tool-kit/qualitative-text/coding";

const BOM = "﻿";
const CSV = "text/csv;charset=utf-8";

interface ExportStrings {
  exportHeading: string;
  exportHint: string;
  exportCsv: string;
  exportMarkdown: string;
  exportCodebook: string;
  exportProject: string;
  importProject: string;
  importFailed: string;
  clearAll: string;
  clearAllConfirm: string;
  csvHead: {
    document: string;
    code: string;
    excerpt: string;
    start: string;
    end: string;
    memo: string;
  };
  codebookHead: {
    code: string;
    parent: string;
    passages: string;
    total: string;
  };
  markdownTitle: string;
  markdownCharacters: string;
  markdownEmpty: string;
  filenames: {
    csv: string;
    markdown: string;
    codebook: string;
    project: string;
  };
}

export function CodingExport({
  project,
  strings,
  importFailed,
  confirmClear,
  onImport,
  onClear,
}: {
  project: Project;
  strings: ExportStrings;
  importFailed: boolean;
  confirmClear: boolean;
  onImport: (file: File | undefined) => void;
  onClear: () => void;
}) {
  const rows = excerptsFor(project, null, false);
  const head = strings.csvHead;
  const nothing = project.documents.length === 0 && project.codes.length === 0;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H2}>{strings.exportHeading}</h3>
      <p className={HINT}>{strings.exportHint}</p>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length === 0}
          onClick={() =>
            downloadText(
              strings.filenames.csv,
              BOM +
                excerptsCsv(rows, [
                  head.document,
                  head.code,
                  head.excerpt,
                  head.start,
                  head.end,
                  head.memo,
                ]),
              CSV
            )
          }
          type="button"
        >
          {strings.exportCsv}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={project.codes.length === 0}
          onClick={() =>
            downloadText(
              strings.filenames.markdown,
              excerptsMarkdown(project, {
                title: strings.markdownTitle,
                characters: strings.markdownCharacters,
                noExcerpts: strings.markdownEmpty,
              }),
              "text/markdown;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.exportMarkdown}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={project.codes.length === 0}
          onClick={() =>
            downloadText(
              strings.filenames.codebook,
              BOM +
                codebookCsv(project, [
                  strings.codebookHead.code,
                  strings.codebookHead.parent,
                  strings.codebookHead.passages,
                  strings.codebookHead.total,
                ]),
              CSV
            )
          }
          type="button"
        >
          {strings.exportCodebook}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={nothing}
          onClick={() =>
            downloadText(
              strings.filenames.project,
              serializeProject(project),
              "application/json;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.exportProject}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className={LABEL} htmlFor="qc-import">
          {strings.importProject}
        </label>
        <input
          accept="application/json,.json"
          className="text-foreground text-sm"
          id="qc-import"
          onChange={(event) => {
            onImport(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
        <button
          className={BUTTON_SECONDARY}
          disabled={nothing}
          onClick={onClear}
          type="button"
        >
          {confirmClear ? strings.clearAllConfirm : strings.clearAll}
        </button>
      </div>
      {importFailed ? <ErrorNotice>{strings.importFailed}</ErrorNotice> : null}
    </section>
  );
}
